import { useEffect, useState } from "react";
import { Button } from "@component/button";
import {
    Card,
    CardContent,
} from "@component/card";
import { useNavigate, useParams } from "react-router-dom";
import { Input } from "@component/input";
import { Label } from "@component/label";
import { getAssessmentCompanyDemo } from "@service/assessment";
import { DemoCompanyInfo } from "@type/company";
import BasicLayout from "@container/layout/basic";
import { blackListDomain } from "@constant/domina";

export default function AIInterviewDemo() {
    const navigate = useNavigate();
    const { companyname } = useParams()
    const [isStarting, setIsStarting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [formData, setFormData] = useState({
        yourName: "",
        email: "",
    });
    const [companyInfo, setCompanyInfo] = useState<DemoCompanyInfo | undefined>(undefined);

    useEffect(() => {
        if (companyname) {
            getAssessmentCompanyDemo(companyname).then((resp) => {
                if (resp.status === 200) {
                    setCompanyInfo(resp.data.data);;
                }
            })
        }
    }, [companyname])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    // Check if all form fields are filled
    const areAllFieldsFilled = () => {
        return Object.values(formData).every(value => value.trim() !== "");
    };

    const handleStartInterview = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        // Only proceed if all fields are filled
        if (!areAllFieldsFilled()) return;

        const isBlacklisted = blackListDomain.some((domain) =>
            formData.email.toLowerCase().endsWith(domain)
        );
        const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
        if (isBlacklisted) {
            setError(labels.emailError);
            return
        } else if (!isValidEmail) {
            setError(labels.invalidEmailError);
            return
        }

        try {
            setIsStarting(true);
            setError(null);
            // Store form data before navigating
            localStorage.setItem(`demoCompanyName:${companyname}`, JSON.stringify(formData));

            navigate(`/demo/${companyname}/interview`);
        } catch (error) {
            setError("Failed to start the interview. Please try again.");
            console.error("Error starting interview:", error);
        }
    };

    const isFormValid = formData.yourName.trim() !== "" && formData.email.trim() !== "";

    // Check if we need Indonesian translation
    const isIndonesian = companyname === "kopi-kenangan";

    // Translation labels
    const labels = {
        title: isIndonesian ? "Demo Wawancara AI" : "AI Interviewer Demo",
        subtitle: isIndonesian
            ? `Demo khusus untuk ${companyInfo?.company_name || ""}.`
            : `A personalized demo for ${companyInfo?.company_name || ""}.`,
        description: isIndonesian
            ? "Demo ini menunjukkan cara kerja Wawancara AI ourteam."
            : "This demo showcases how ourteam's AI Interview works.",
        yourNameLabel: isIndonesian ? "Nama Lengkap" : "Your Name",
        yourNamePlaceholder: isIndonesian ? "Masukkan nama lengkap Anda" : "Your name",
        emailLabel: "Email",
        emailPlaceholder: isIndonesian ? "nama@perusahaan.com" : "your.email@example.com",
        privacyText: isIndonesian
            ? "Data Anda aman dan tidak akan dibagikan kepada pihak mana pun."
            : "Your data is kept private and will not be shared with anyone.",
        contactText: isIndonesian ? "Untuk pertanyaan lebih lanjut, hubungi" : "For enquiries, contact",
        proceedButton: isIndonesian ? "Mulai" : "Proceed",
        preparingCall: isIndonesian ? "Sedang mempersiapkan..." : "Preparing Call...",
        emailError: isIndonesian ? "Mohon gunakan email perusahaan." : "Please enter company email address.",
        invalidEmailError: isIndonesian ? "Format email tidak valid." : "Please enter a valid email address.",
        startError: isIndonesian ? "Gagal memulai wawancara. Silakan coba lagi." : "Failed to start the interview. Please try again.",
    };

    return (
        <BasicLayout
            hideNavbarMenu
            disableMinHeight
            customLogo={`${import.meta.env.VITE_ASSET_OURTEAM_BASE_URL}/commons/ourteam_logo_only.png`}
            cssRoot="!pb-0"
        >
            <div className="text-center z-10 w-full">
                <div className="bg-gradient-to-br from-gray-900 via-black to-gray-800 w-full py-12 px-4 rounded-b-[3rem] shadow-2xl border-b border-gray-700 pt-16 pb-72 gap-8">
                    <div className="flex justify-center mb-8">
                        <img
                            src={companyInfo?.company_logo || ""}
                            alt={companyInfo?.company_name || ""}
                            className="h-16 w-auto object-contain"
                        />
                    </div>
                    <h1 className="text-2xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight whitespace-nowrap bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text text-transparent">
                        {labels.title}
                    </h1>
                    {/* Subtitle moved from form component */}
                    <div className="flex items-center justify-center mt-4">
                        <span className="text-base text-gray-300 font-medium tracking-wide pt-0">
                            {labels.subtitle}
                        </span>
                    </div>
                </div>
            </div>

            <div className="absolute z-20 flex justify-center w-full px-4 transform transition-all duration-300 top-[300px] sm:top-[320px] bg-transparent">
                <div className="w-full max-w-lg">
                    {/* Main content card */}
                    <Card className="bg-white/95 backdrop-blur-sm shadow-2xl border border-gray-200/50 hover:shadow-3xl transition-all duration-300 transform hover:-translate-y-1">
                        <CardContent
                            className="pt-3 pb-6 px-6 bg-white"
                            style={{
                                borderRadius: "1rem",
                                paddingRight: "32px",
                                paddingLeft: "32px",
                                paddingTop: "24px",
                            }}
                        >
                            {/* Description */}
                            <p
                                className="text-sm text-gray-600 text-center mb-6 leading-relaxed font-medium"
                            >
                                {labels.description}
                            </p>

                            {/* Form */}
                            <form onSubmit={handleStartInterview} className="space-y-5">
                                <div className="space-y-2">
                                    <Label
                                        htmlFor="yourName"
                                        className="text-sm font-medium text-gray-800"
                                    >
                                        {labels.yourNameLabel}
                                    </Label>
                                    <Input
                                        id="yourName"
                                        type="text"
                                        name="yourName"
                                        placeholder={labels.yourNamePlaceholder}
                                        value={formData.yourName}
                                        onChange={handleInputChange}
                                        className="h-11 bg-gray-50/80 border-gray-200 text-black placeholder:text-gray-500 focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all duration-200 hover:bg-gray-50"
                                        required
                                    />
                                </div>

                                <div className="space-y-2">
                                    <Label
                                        htmlFor="email"
                                        className="text-sm font-medium text-gray-800"
                                    >
                                        {labels.emailLabel}
                                    </Label>
                                    <Input
                                        id="email"
                                        type="email"
                                        name="email"
                                        placeholder={labels.emailPlaceholder}
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="h-11 bg-gray-50/80 border-gray-200 text-black placeholder:text-gray-500 focus:border-blue-400 focus:ring-2 focus:ring-blue-400/20 transition-all duration-200 hover:bg-gray-50"
                                        required
                                    />
                                </div>

                                {/* Info text */}
                                <div className="text-center text-sm text-gray-600 leading-relaxed">
                                    <p>
                                        {labels.privacyText}
                                        <br />
                                        {labels.contactText} <span className="underline">hello@ourteam.ai</span>.
                                    </p>
                                </div>

                                {error && (
                                    <div className="text-red-500 mb-4 text-center">
                                        {error}
                                    </div>
                                )}

                                {/* Submit button */}
                                <Button
                                    disabled={isStarting || !areAllFieldsFilled()}
                                    className={`w-full h-12 font-medium rounded-full transition-all duration-300 transform hover:scale-[1.02] active:scale-[0.98] ${isFormValid
                                        ? "bg-gradient-to-r from-gray-900 to-black text-white hover:from-black hover:to-gray-900 shadow-lg hover:shadow-xl"
                                        : "bg-gray-300 text-gray-500 cursor-not-allowed"
                                        }`}
                                >
                                    {isStarting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                            {labels.preparingCall}
                                        </>
                                    ) : labels.proceedButton}
                                </Button>
                            </form>

                            {/* Footer */}
                            <div className="text-center mt-6" style={{ paddingBottom: "16px" }}>
                                <img
                                    src="https://assets.polymet.ai/nervous-tan-828931"
                                    alt="Powered by ourteam"
                                    className="mx-auto h-12 w-auto opacity-80 hover:opacity-100 transition-opacity cursor-pointer"
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>
        </BasicLayout>
    );
}
